import type { DefaultError } from '@tanstack/react-query'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { SUPPORT_EMAIL } from '../../constants'
import { ApiError } from '../api-client/api-error'
import { ZodParseError } from '../api-client/api-hooks'
import * as styles from './error-detail.css'
import { getErrorMessage } from './utils'

const buildSupportMailtoHref = (error: DefaultError) => {
  const { subject, body } = match(error)
    .when(
      (e): e is ZodParseError => e instanceof ZodParseError,
      e => ({
        subject: `Unexpected issue: ${e.path}`,
        body: [
          `Endpoint: ${e.path}`,
          e.schemaName ? `Schema: ${e.schemaName}` : undefined,
          '',
          'Zod issues:',
          JSON.stringify(e.issues, null, 2),
        ],
      })
    )
    .when(
      (e): e is ApiError => e instanceof ApiError,
      e => ({
        subject: `Error: ${e.path ?? 'unknown endpoint'} (${e.status})`,
        body: [
          e.path ? `Endpoint: ${e.path}` : undefined,
          `Status: ${e.status}`,
          e.problemTitle ? `Title: ${e.problemTitle}` : undefined,
          e.problemDetail ? `Detail: ${e.problemDetail}` : undefined,
        ],
      })
    )
    .otherwise(e => ({
      subject: 'Unexpected error',
      body: [`Error: ${e.message}`, e.stack ? '' : undefined, e.stack ? 'Stack trace:' : undefined, e.stack],
    }))

  const params = new URLSearchParams({ subject, body: body.filter(line => line !== undefined).join('\n') })
  return `mailto:${SUPPORT_EMAIL}?${params.toString()}`
}

const useSupportMailtoHref = (error: DefaultError) => useMemo(() => buildSupportMailtoHref(error), [error])

type ErrorDetailProps = {
  error: DefaultError
}

export const ErrorDetail: React.FC<ErrorDetailProps> = ({ error }) => {
  const message = getErrorMessage(error)
  const href = useSupportMailtoHref(error)

  return (
    <span>
      {message} If this keeps happening,{' '}
      <a href={href} className={styles.contactSupportLink}>
        contact Support
      </a>
      .
    </span>
  )
}
